/**
 * #4100: reading the "foods you have logged before" list that the Add Foods page shows
 * when the search box is empty.
 *
 * Why this is its own module. Opening Add Foods runs a search with an empty search box,
 * whose only job is to list the foods the user has logged before. That list normally comes
 * from a PouchDB map/reduce view called `userFoodsUsed`. On iOS native the local database
 * is stored through our own Capacitor SQLite adapter, and building that view's index for
 * the first time there is an unbounded amount of work with no time limit anywhere in the
 * path. When it does not answer, the page shows a spinner and nothing else, forever.
 *
 * So this module never waits for either read indefinitely. It gives the view a limited amount
 * of time, and if the view has not answered by then — or if it fails outright — it reads the
 * same foods a second way instead, under a limit of its own. Both limits matter: the leading
 * explanation for the iOS symptom is that the whole local database is slow to answer, and if
 * that is right then the substitute read can be slow too. Bounding only the first one would
 * leave the endless spinner reachable by a second route.
 *
 * The two reads give the page the same foods to show, with one documented difference. The
 * view emits only documents whose id starts with `food/` and that have a last-used date or a
 * "liked" mark (see `userFoodsUsed` in user-db.service.ts). Reading all of the user's foods
 * returns more: everything with no last-used date and no "liked" mark, which the Add Foods
 * page then drops itself whenever the search box is empty (see rankHeadersForDisplay in
 * foods.ts) — AND the user's legacy saved-meal documents, whose ids start with `meal/`, which
 * the page does NOT drop. So on the substitute path the list can additionally contain legacy
 * saved meals the user has used before. That is not a new kind of entry appearing from
 * nowhere: every TYPED search already shows them, because a typed search performs exactly
 * this same read. Which is also why we know the substitute read works on iOS.
 */

import { describeError } from '@cf-shared/error-describe';
import type { Food } from '@cf-shared/data-model/food';

/**
 * How long the view is given to answer before we stop waiting for it and read the foods the
 * other way. Compared against how long the `userFoodsUsed` view query takes: a view whose
 * index is already built answers in well under a second, so a healthy device never reaches
 * this limit and always takes the fast path. Crossing it does not fail anything — the user
 * still gets their list, just read the slower way.
 */
export const DEFAULT_FOODS_VIEW_WAIT_MS = 4_000;

/**
 * How long the substitute read is then given. Compared against how long a plain
 * document-prefix read of every food takes, which is the same read a typed search performs on
 * every keystroke batch — so on any device where searching works at all, this limit is never
 * reached. Crossing it means the local database is not answering at all, and the page is told
 * the list could not be read, so it shows a failure state with a Retry rather than a spinner
 * that never stops. Longer than the view's limit because this read is the last chance to
 * produce a list, so it is worth waiting a bit longer before giving up on it.
 */
export const DEFAULT_FOODS_FALLBACK_WAIT_MS = 8_000;

/** Which of the two reads actually produced the list. */
export enum DefaultFoodsSource {
  /** the fast path: the `userFoodsUsed` view answered in time */
  UsedFoodsView = 'used-foods-view',
  /** the view was too slow or failed, so every food was read instead and filtered later */
  AllFoodsFallback = 'all-foods-fallback',
}

export interface DefaultFoodsResult {
  foods: Food[];
  source: DefaultFoodsSource;
}

/** Raised when neither read could produce the list, so the page must say so to the user. */
export class DefaultFoodsUnavailableError extends Error {
  constructor(public readonly viewError: unknown, public readonly fallbackError: unknown) {
    super('Could not read the foods logged before: both the used-foods view and the all-foods read failed');
    this.name = 'DefaultFoodsUnavailableError';
  }
}

export interface DefaultFoodsDeps {
  /** the fast read — the `userFoodsUsed` map/reduce view */
  readUsedFoodsViewAsync: () => Promise<Food[]>;
  /** the substitute read — every food the user has, filtered down by the page afterwards */
  readAllFoodsAsync: () => Promise<Food[]>;
  /** how long the view is given to answer (DEFAULT_FOODS_VIEW_WAIT_MS in the app) */
  viewWaitMs: number;
  /** how long the substitute read is then given (DEFAULT_FOODS_FALLBACK_WAIT_MS in the app) */
  fallbackWaitMs: number;
  /** injectable so the unit test can drive the clock by hand instead of really waiting */
  scheduleTimeout: (callback: () => void, ms: number) => unknown;
  clearScheduledTimeout: (handle: unknown) => void;
  /** injectable clock, so the reported wait time is deterministic in the test */
  nowMs: () => number;
  /**
   * The substitute read carried the list, so nothing is wrong for the user — this is the
   * self-heal tier (LoggingService.trackSilentInfo). It is also the only way we learn, from
   * real devices, how often the view fails to answer and why, since the iOS symptom cannot
   * be reproduced on the web.
   */
  reportSelfHeal: (name: string, context: Record<string, unknown>) => void;
  /** neither read worked, so the user is looking at a failure state (trackSilentProblem) */
  reportProblem: (name: string, context: Record<string, unknown>) => void;
}

/** why we stopped waiting for the view — reported so the field tells us which one iOS hits */
type FallbackTrigger = 'timed-out' | 'view-failed';

/** what a bounded read produced: the foods, a failure, or nothing before the limit was reached */
type BoundedReadOutcome =
  | { kind: 'answered'; foods: Food[] }
  | { kind: 'failed'; error: unknown }
  | { kind: 'timed-out' };

/** the error recorded for a read that never came back, so telemetry says which limit was hit */
class ReadTimedOutError extends Error {
  constructor(waitMs: number, whichRead: string) {
    super(`the ${whichRead} did not answer within ${waitMs}ms`);
    this.name = 'ReadTimedOutError';
  }
}

/**
 * Reads the "logged before" foods for an empty search box.
 *
 * Resolves with the foods and which read produced them. Rejects with
 * DefaultFoodsUnavailableError when NEITHER read could produce a list — whether it failed or
 * simply never answered. It never waits indefinitely on either read, which is the property
 * that makes an endless spinner impossible for the caller.
 *
 * Neither read is cancelled when we stop waiting for it — a PouchDB query cannot be cancelled
 * anyway, and letting the view's index build finish in the background is what makes the next
 * visit to the page fast.
 */
export async function loadDefaultFoodsAsync(deps: DefaultFoodsDeps): Promise<DefaultFoodsResult> {
  const startedAtMs = deps.nowMs();
  const viewOutcome = await runBoundedReadAsync(deps, deps.readUsedFoodsViewAsync, deps.viewWaitMs);

  if (viewOutcome.kind === 'answered') {
    return { foods: viewOutcome.foods, source: DefaultFoodsSource.UsedFoodsView };
  }

  const trigger: FallbackTrigger = viewOutcome.kind === 'failed' ? 'view-failed' : 'timed-out';
  const viewError = viewOutcome.kind === 'failed'
    ? viewOutcome.error
    : new ReadTimedOutError(deps.viewWaitMs, 'used-foods view read');

  const fallbackOutcome = await runBoundedReadAsync(deps, deps.readAllFoodsAsync, deps.fallbackWaitMs);
  const waitedMs = deps.nowMs() - startedAtMs;

  if (fallbackOutcome.kind !== 'answered') {
    // neither read worked, so there is no list to show and the page has to say so
    const fallbackError = fallbackOutcome.kind === 'failed'
      ? fallbackOutcome.error
      : new ReadTimedOutError(deps.fallbackWaitMs, 'all-foods read');
    deps.reportProblem('FoodSearchDefaultFoodsUnavailable', {
      trigger,
      waitedMs,
      viewError: describeForReport(viewError),
      fallbackError: describeForReport(fallbackError),
    });
    throw new DefaultFoodsUnavailableError(viewError, fallbackError);
  }

  // the substitute read carried the list, so nothing is wrong for the user — but this is the
  // only visibility we have into how often the view fails to answer on real iOS devices
  deps.reportSelfHeal('FoodSearchDefaultFoodsFellBack', {
    trigger,
    waitedMs,
    foodCount: fallbackOutcome.foods.length,
    viewError: describeForReport(viewError),
  });

  return { foods: fallbackOutcome.foods, source: DefaultFoodsSource.AllFoodsFallback };
}

/**
 * Runs one read, but waits no longer than `waitMs` for it. Never rejects: a read that fails
 * comes back as a `failed` outcome and one that does not answer in time as `timed-out`, so
 * the caller decides what each one means rather than having to catch.
 */
function runBoundedReadAsync(
  deps: DefaultFoodsDeps,
  read: () => Promise<Food[]>,
  waitMs: number,
): Promise<BoundedReadOutcome> {
  return new Promise<BoundedReadOutcome>(resolve => {
    let alreadyAnswered = false;
    let waitHandle: unknown;
    const finish = (outcome: BoundedReadOutcome) => {
      if (alreadyAnswered) return;
      alreadyAnswered = true;
      deps.clearScheduledTimeout(waitHandle);
      resolve(outcome);
    };

    waitHandle = deps.scheduleTimeout(() => finish({ kind: 'timed-out' }), waitMs);

    // start the read AFTER the timer is armed, so the handle always exists by the time
    // `finish` runs and the timer is never left behind. `read` is called inside this try so a
    // read that throws SYNCHRONOUSLY becomes a `failed` outcome rather than escaping the
    // executor, which would reject this promise and break the never-rejects contract above.
    try {
      read().then(
        foods => finish({ kind: 'answered', foods }),
        error => finish({ kind: 'failed', error }),
      );
    } catch (error) {
      finish({ kind: 'failed', error });
    }
  });
}

/** a short, safe description of an error for a telemetry field; undefined when there was none */
function describeForReport(error: unknown): string | undefined {
  return error === undefined || error === null ? undefined : describeError(error);
}
